import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  IconButton,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  AutoFixHigh as AIIcon,
  Psychology as BrainIcon,
  Edit as EditIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { aiEnhancementService, AIEnhancementRequest, AIEnhancementResult, AIEditableField } from '../services/aiEnhancementService';

interface AIEnhancementPanelProps {
  open: boolean;
  onClose: () => void;
  table: string;
  recordId?: number;
  formData?: Record<string, any>;
  onFieldUpdate?: (fieldName: string, value: string) => void;
  onBulkUpdate?: (updates: Record<string, string>) => void;
  onSave?: () => Promise<void>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

export const AIEnhancementPanel: React.FC<AIEnhancementPanelProps> = ({
  open,
  onClose,
  table,
  recordId,
  formData = {},
  onFieldUpdate,
  onBulkUpdate,
  onSave
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [editableFields, setEditableFields] = useState<AIEditableField[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Individual field enhancement state
  const [selectedField, setSelectedField] = useState<string>('');
  const [enhancementMode, setEnhancementMode] = useState<'generate' | 'improve'>('improve');
  const [fieldResult, setFieldResult] = useState<AIEnhancementResult | null>(null);
  
  // Bulk enhancement state
  const [bulkMode, setBulkMode] = useState<'generate' | 'improve'>('improve');
  const [bulkResults, setBulkResults] = useState<Record<string, AIEnhancementResult> | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);

  // Load AI editable fields when panel opens
  useEffect(() => {
    if (open && table) {
      loadEditableFields();
    }
  }, [open, table]);

  const loadEditableFields = async () => {
    try {
      setLoading(true);
      setError(null);
      const fields = await aiEnhancementService.getAIEditableFields(table);
      setEditableFields(fields);
      if (fields.length > 0 && !selectedField) {
        setSelectedField(fields[0].field_name);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load AI editable fields');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldEnhancement = async () => {
    if (!selectedField) return;

    try {
      setLoading(true);
      setError(null);
      setFieldResult(null);

      const request: AIEnhancementRequest = {
        table,
        recordId,
        fieldName: selectedField,
        currentValue: formData[selectedField] || '',
        mode: enhancementMode
      };

      const result = await aiEnhancementService.enhanceField(request);
      setFieldResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enhance field');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEnhancement = async () => {
    if (!recordId) {
      setError('Record ID is required for bulk enhancement');
      return;
    }

    try {
      setBulkLoading(true);
      setError(null);
      setBulkResults(null);

      const results = await aiEnhancementService.bulkEnhanceRecord({
        table,
        recordId,
        mode: bulkMode
      });

      setBulkResults(results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enhance record');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleApplyFieldEnhancement = () => {
    if (fieldResult && onFieldUpdate) {
      onFieldUpdate(selectedField, fieldResult.enhanced_content);
      setFieldResult(null);
    }
  };

  const handleApplyBulkEnhancement = async () => {
    if (!bulkResults || !onBulkUpdate) return;

    try {
      setBulkSaving(true);
      setError(null);

      // Apply the updates to form data
      const updates: Record<string, string> = {};
      Object.entries(bulkResults).forEach(([fieldName, result]) => {
        updates[fieldName] = result.enhanced_content;
      });
      onBulkUpdate(updates);

      // Save the record if onSave is provided
      if (onSave) {
        await onSave();
      }

      // Clear results and close panel on successful save
      setBulkResults(null);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save enhanced content');
    } finally {
      setBulkSaving(false);
    }
  };

  const selectedFieldConfig = editableFields.find(f => f.field_name === selectedField);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 600 }
      }}
    >
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AIIcon color="primary" />
            <Typography variant="h6">AI Enhancement</Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Table Info */}
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ py: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Table: <strong>{table}</strong>
              {recordId && (
                <>
                  {' '} | Record ID: <strong>{recordId}</strong>
                </>
              )}
            </Typography>
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab label="Field Enhancement" icon={<EditIcon />} />
            <Tab label="Bulk Enhancement" icon={<BrainIcon />} disabled={!recordId} />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {/* Individual Field Enhancement */}
          <TabPanel value={activeTab} index={0}>
            {loading && editableFields.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {editableFields.length === 0 ? (
                  <Alert severity="info">
                    No AI editable fields found for this table.
                  </Alert>
                ) : (
                  <>
                    {/* Field Selection */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Select Field</InputLabel>
                      <Select
                        value={selectedField}
                        onChange={(e) => setSelectedField(e.target.value)}
                        label="Select Field"
                      >
                        {editableFields.map((field) => (
                          <MenuItem key={field.field_name} value={field.field_name}>
                            {field.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Enhancement Mode */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Enhancement Mode</InputLabel>
                      <Select
                        value={enhancementMode}
                        onChange={(e) => setEnhancementMode(e.target.value as 'generate' | 'improve')}
                        label="Enhancement Mode"
                      >
                        <MenuItem value="improve">
                          <Box>
                            <Typography variant="body2">Improve Existing</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Enhance current content
                            </Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="generate">
                          <Box>
                            <Typography variant="body2">Generate New</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Create content from scratch
                            </Typography>
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>

                    {/* Field Context Info */}
                    {selectedFieldConfig && (
                      <Accordion sx={{ mb: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InfoIcon fontSize="small" />
                            <Typography variant="body2">Field Context</Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography variant="body2" color="textSecondary">
                            {selectedFieldConfig.ai_context_prompt}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Current Value Display */}
                    {formData[selectedField] && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          Current Value:
                        </Typography>
                        <TextField
                          multiline
                          rows={3}
                          value={formData[selectedField] || ''}
                          InputProps={{ readOnly: true }}
                          fullWidth
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    )}

                    {/* Enhancement Button */}
                    <Button
                      variant="contained"
                      onClick={handleFieldEnhancement}
                      disabled={loading || !selectedField}
                      fullWidth
                      sx={{ mb: 2 }}
                      startIcon={loading ? <CircularProgress size={20} /> : <AIIcon />}
                    >
                      {loading ? 'Enhancing...' : `${enhancementMode === 'generate' ? 'Generate' : 'Improve'} Content`}
                    </Button>

                    {/* Result Display */}
                    {fieldResult && (
                      <Card sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            AI Enhanced Content
                          </Typography>
                          <TextField
                            multiline
                            rows={6}
                            value={fieldResult.enhanced_content}
                            fullWidth
                            variant="outlined"
                            InputProps={{ readOnly: true }}
                            sx={{ mb: 2 }}
                          />
                          
                          {/* Context Info */}
                          <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="body2">Enhancement Details</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" color="textSecondary">
                                  Context Used:
                                </Typography>
                                <Typography variant="body2">
                                  {fieldResult.context_used.join(', ')}
                                </Typography>
                              </Box>
                              {fieldResult.search_results_used && fieldResult.search_results_used.length > 0 && (
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="caption" color="textSecondary">
                                    Best Practices Applied:
                                  </Typography>
                                  <Typography variant="body2">
                                    {fieldResult.search_results_used.join(', ')}
                                  </Typography>
                                </Box>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        </CardContent>
                        <CardActions>
                          <Button
                            variant="contained"
                            onClick={handleApplyFieldEnhancement}
                            startIcon={<AddIcon />}
                          >
                            Apply to Field
                          </Button>
                          <Button onClick={() => setFieldResult(null)}>
                            Discard
                          </Button>
                        </CardActions>
                      </Card>
                    )}
                  </>
                )}
              </>
            )}
          </TabPanel>

          {/* Bulk Enhancement */}
          <TabPanel value={activeTab} index={1}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Bulk enhancement will process all AI-editable fields for this record.
            </Alert>

            {/* Enhancement Mode */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Enhancement Mode</InputLabel>
              <Select
                value={bulkMode}
                onChange={(e) => setBulkMode(e.target.value as 'generate' | 'improve')}
                label="Enhancement Mode"
              >
                <MenuItem value="improve">Improve All Fields</MenuItem>
                <MenuItem value="generate">Generate All Fields</MenuItem>
              </Select>
            </FormControl>

            {/* Fields to Process */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Fields to be enhanced ({editableFields.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {editableFields.map((field) => (
                  <Chip
                    key={field.field_name}
                    label={field.label}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>

            {/* Bulk Enhancement Button */}
            <Button
              variant="contained"
              onClick={handleBulkEnhancement}
              disabled={bulkLoading || editableFields.length === 0}
              fullWidth
              sx={{ mb: 2 }}
              startIcon={bulkLoading ? <CircularProgress size={20} /> : <BrainIcon />}
            >
              {bulkLoading ? 'Processing...' : `${bulkMode === 'generate' ? 'Generate' : 'Improve'} All Fields`}
            </Button>

            {/* Bulk Results */}
            {bulkResults && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Bulk Enhancement Results
                  </Typography>
                  {Object.entries(bulkResults).map(([fieldName, result]) => (
                    <Accordion key={fieldName}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2">
                          {editableFields.find(f => f.field_name === fieldName)?.label || fieldName}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TextField
                          multiline
                          rows={4}
                          value={result.enhanced_content}
                          fullWidth
                          variant="outlined"
                          InputProps={{ readOnly: true }}
                        />
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    onClick={handleApplyBulkEnhancement}
                    disabled={bulkSaving}
                    startIcon={bulkSaving ? <CircularProgress size={20} /> : <AddIcon />}
                  >
                    {bulkSaving ? 'Saving...' : 'Apply All Changes'}
                  </Button>
                  <Button 
                    onClick={() => setBulkResults(null)}
                    disabled={bulkSaving}
                  >
                    Discard
                  </Button>
                </CardActions>
              </Card>
            )}
          </TabPanel>
        </Box>
      </Box>
    </Drawer>
  );
}; 